import { Link } from "react-router-dom";
import { Sparkles, LayoutGrid, Palette, Box, ArrowRight } from "lucide-react";
import { Reveal, SectionLabel } from "../components/Reveal";

const PREVIEW = [
  {
    icon: LayoutGrid,
    title: "Space planning",
    desc: "Upload your floor plan and explore layout ideas tailored to your BHK, lifestyle, and budget.",
  },
  {
    icon: Palette,
    title: "Style & materials",
    desc: "Preview palettes, finishes, and package tiers—from Modern to Luxury—before you commit.",
  },
  {
    icon: Box,
    title: "3D visualization",
    desc: "See mood boards and concept renders that bring your rooms to life in minutes, not weeks.",
  },
];

export default function AiDesigner() {
  return (
    <div className="pt-32 pb-24">
      <section className="max-w-7xl mx-auto px-6">
        <Reveal>
          <span className="inline-flex items-center gap-2 rounded-full border border-accent/40 bg-accent/10 px-4 py-1.5 text-xs font-medium text-accent label-caps">
            <Sparkles size={14} />
            Coming soon
          </span>
        </Reveal>
        <Reveal delay={0.08} className="mt-8">
          <SectionLabel>AI Designer</SectionLabel>
        </Reveal>
        <Reveal delay={0.12}>
          <h1 className="font-serif-display text-5xl md:text-6xl mt-6 max-w-3xl leading-tight">
            Your home, imagined with intelligence.
          </h1>
        </Reveal>
        <Reveal delay={0.18}>
          <p className="mt-8 max-w-2xl text-lg text-muted-foreground leading-relaxed">
            We&apos;re building an AI-assisted design experience for Kalpavriksha clients—quick concepts,
            package-aware estimates, and visual previews that complement our human designers and site
            visits. It won&apos;t replace expert craftsmanship; it will help you start smarter.
          </p>
        </Reveal>
        <Reveal delay={0.22}>
          <p className="mt-4 max-w-2xl text-muted-foreground leading-relaxed">
            Launching first for Telangana and Andhra Pradesh projects. Join the waitlist by reaching out—we&apos;ll
            notify you when beta access opens.
          </p>
        </Reveal>
        <Reveal delay={0.26} className="flex flex-wrap gap-4 mt-10">
          <Link
            to="/contact"
            data-testid="ai-designer-contact"
            className="inline-flex items-center gap-2 rounded-full bg-primary text-primary-foreground px-6 py-3 text-sm font-medium hover:bg-accent hover:text-accent-foreground transition-colors"
          >
            Notify me <ArrowRight size={16} />
          </Link>
          <Link
            to="/calculator"
            className="inline-flex items-center gap-2 rounded-full border border-border px-6 py-3 text-sm font-medium hover:border-accent hover:text-accent transition-colors"
          >
            Try cost calculator
          </Link>
        </Reveal>
      </section>

      <section className="max-w-7xl mx-auto px-6 mt-20 md:mt-28">
        <Reveal>
          <h2 className="font-serif-display text-3xl md:text-4xl">What to expect</h2>
        </Reveal>
        <div className="grid md:grid-cols-3 gap-6 mt-12">
          {PREVIEW.map((item, i) => (
            <Reveal key={item.title} delay={i * 0.08}>
              <div className="h-full rounded-2xl border border-border bg-card p-8">
                <span className="grid place-items-center w-12 h-12 rounded-xl bg-primary text-primary-foreground mb-5">
                  <item.icon size={22} />
                </span>
                <h3 className="font-serif-display text-xl">{item.title}</h3>
                <p className="mt-3 text-muted-foreground leading-relaxed text-sm">{item.desc}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 mt-20">
        <Reveal>
          <div className="rounded-3xl border border-dashed border-accent/50 bg-secondary/30 px-8 py-12 md:px-14 md:py-16 text-center">
            <p className="label-caps text-muted-foreground">Status</p>
            <p className="font-serif-display text-2xl md:text-3xl mt-3">In development</p>
            <p className="mt-4 max-w-xl mx-auto text-muted-foreground text-sm leading-relaxed">
              Features and timelines will be announced here. For immediate help with quotations,
              packages, or consultations, use our assistant on the site or WhatsApp.
            </p>
          </div>
        </Reveal>
      </section>
    </div>
  );
}
